/**
 * Skill Builder workspace — an interactive, agent-assisted "author a new skill"
 * experience. Opened as a full-screen overlay from the Catalog.
 *
 *   Launcher  → describe the skill + Start (or resume an in-progress build)
 *   Workspace → chat transcript (left) + live staging tree & file viewer (right),
 *               status pill, send-next-turn composer, Cancel, Approve & Import.
 *
 * Transcript + staged tree are derived from GET /api/skill-builder/:id (the
 * authoritative NDJSON log), polled while a turn is running — simple and
 * dedup-free. The bridge owns the sandboxed claude-sub spawns; everything here
 * is proxied through agent-ui.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles, X, Send, FileText, Ban, PackageCheck, Loader2, ArrowLeft, Wrench } from "lucide-react";
import { SkillBuilderApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";
import { Button, Pill, Spinner, EmptyState } from "./primitives";
import { cn } from "../lib/cn";
import type { BuilderMeta, BuilderStatus } from "../lib/types";

const STATUS_TONE: Record<BuilderStatus, "neutral" | "success" | "warning" | "danger"> = {
  running: "warning",
  awaiting_user: "success",
  failed: "danger",
  cancelled: "neutral",
};
const STATUS_LABEL: Record<BuilderStatus, string> = {
  running: "thinking…",
  awaiting_user: "ready",
  failed: "failed",
  cancelled: "cancelled",
};

interface Msg { role: "assistant" | "tool" | "user"; text: string }

/** Parse the stream-json NDJSON log into a readable transcript. */
function parseTranscript(log: string): Msg[] {
  const out: Msg[] = [];
  for (const line of log.split("\n")) {
    if (!line.trim()) continue;
    let ev: Record<string, unknown>;
    try { ev = JSON.parse(line); } catch { continue; }
    if (ev.type === "assistant") {
      const content = (ev.message as { content?: unknown[] })?.content ?? [];
      for (const c of content as Array<Record<string, unknown>>) {
        if (c.type === "text" && typeof c.text === "string" && c.text.trim()) out.push({ role: "assistant", text: c.text });
        else if (c.type === "tool_use") {
          const fp = (c.input as { file_path?: string })?.file_path;
          out.push({ role: "tool", text: fp ? `drafted ${fp}` : String(c.name ?? "tool") });
        }
      }
    }
  }
  return out;
}

export function SkillBuilder({ onClose, onImported }: { onClose: () => void; onImported: (name: string) => void }) {
  const [activeId, setActiveId] = useState<string | null>(null);
  // Lock the page behind the overlay so scrolling stays inside the builder
  // (otherwise touch/wheel chains through to the page underneath).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return (
    <div className="fixed inset-0 z-50 bg-bg/90 backdrop-blur-sm flex flex-col">
      <div className="h-11 flex items-center gap-2 px-4 border-b border-border flex-shrink-0">
        <Sparkles className="size-4 text-accent" />
        <span className="text-sm font-medium text-fg">Skill Builder</span>
        {activeId && <button onClick={() => setActiveId(null)} className="ml-2 inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg"><ArrowLeft className="size-3" /> all builds</button>}
        <Button size="icon" variant="ghost" className="ml-auto" onClick={onClose} aria-label="Close"><X className="size-4" /></Button>
      </div>
      <div className="flex-1 min-h-0">
        {activeId
          ? <Workspace id={activeId} onImported={(n) => { onImported(n); onClose(); }} />
          : <Launcher onOpen={setActiveId} />}
      </div>
    </div>
  );
}

function Launcher({ onOpen }: { onOpen: (id: string) => void }) {
  const toast = useToast();
  const [goal, setGoal] = useState("");
  const { data: builders = [] } = useQuery({ queryKey: qk.builder.list, queryFn: SkillBuilderApi.list, refetchInterval: 4000 });
  const startMut = useMutation({
    mutationFn: () => SkillBuilderApi.start(goal.trim()),
    onSuccess: (m) => onOpen(m.id),
    onError: (e) => toast.error("Couldn't start", String(e)),
  });
  const inProgress = builders.filter((b) => b.status === "running" || b.status === "awaiting_user");

  return (
    <div className="h-full overflow-y-auto overscroll-contain">
      <div className="max-w-2xl mx-auto px-6 py-8">
        <h2 className="text-lg font-semibold text-fg">Create a skill with an agent</h2>
        <p className="text-sm text-fg-muted mt-1">Describe what the skill should do. A sandboxed helper (claude-sub) will clarify, draft, and iterate with you. Nothing goes live until you review and import.</p>
        <textarea
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          rows={4}
          placeholder="e.g. A skill that looks up a flight's status by number using the AviationStack API and replies with a tidy summary."
          className="mt-4 w-full text-sm rounded-md bg-surface-elevated border border-border p-3 focus:border-border-strong resize-none"
        />
        <div className="mt-3 flex items-center gap-3">
          <Button onClick={() => startMut.mutate()} disabled={goal.trim().length < 8} loading={startMut.isPending}>
            <Sparkles className="size-3.5" /> Start building
          </Button>
          <span className="text-xs text-fg-subtle">backend: claude-sub (subscription)</span>
        </div>

        {inProgress.length > 0 && (
          <div className="mt-8">
            <div className="text-xs uppercase tracking-wide text-fg-subtle mb-2">In progress</div>
            <ul className="space-y-1.5">
              {inProgress.map((b) => (
                <li key={b.id}>
                  <button onClick={() => onOpen(b.id)} className="w-full text-left rounded-md border border-border bg-surface-elevated hover:bg-surface-2 px-3 py-2 flex items-center gap-2">
                    <span className="text-sm text-fg truncate flex-1">{b.goal}</span>
                    <Pill tone={STATUS_TONE[b.status]}>{STATUS_LABEL[b.status]}</Pill>
                    <span className="text-[11px] text-fg-subtle font-mono">{b.turns}t</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Workspace({ id, onImported }: { id: string; onImported: (name: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState("");
  const [pendingUser, setPendingUser] = useState<string[]>([]);
  const [viewFile, setViewFile] = useState<string | null>(null);
  const [mobileTab, setMobileTab] = useState<"chat" | "files">("chat");
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: qk.builder.detail(id),
    queryFn: () => SkillBuilderApi.get(id),
    refetchInterval: (q) => (q.state.data?.meta.status === "running" ? 1500 : false),
  });
  const meta: BuilderMeta | undefined = data?.meta;
  const status = meta?.status;
  const transcript = useMemo(() => parseTranscript(data?.log ?? ""), [data?.log]);
  const staged = data?.staged ?? [];

  // Once the agent has produced new output, the optimistic "you" bubbles are
  // now reflected by a fresh assistant turn — clear them when it's our turn.
  useEffect(() => { if (status === "awaiting_user") setPendingUser([]); }, [status, transcript.length]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [transcript.length, pendingUser.length, status]);
  useEffect(() => { if (!viewFile && staged.includes("SKILL.md")) setViewFile("SKILL.md"); }, [staged, viewFile]);

  const turnMut = useMutation({
    mutationFn: (msg: string) => SkillBuilderApi.turn(id, msg),
    onSuccess: (r, msg) => {
      if (!r.ok) { toast.error("Couldn't send", r.error); return; }
      setPendingUser((p) => [...p, msg]);
      setDraft("");
      qc.invalidateQueries({ queryKey: qk.builder.detail(id) });
    },
  });
  const cancelMut = useMutation({
    mutationFn: () => SkillBuilderApi.cancel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.builder.detail(id) }),
  });
  const importMut = useMutation({
    mutationFn: (allowSecrets?: boolean) => SkillBuilderApi.import(id, { allowSecrets }),
    onSuccess: (r) => { toast.success(`Imported "${r.name}"`, `${r.files} file(s) — assign it from Skills when ready.`); onImported(r.name); },
    onError: (e) => {
      const msg = String(e);
      if (msg.includes("already exists")) { toast.error("Name already exists", "Ask the agent to rename the skill, then import again."); return; }
      if (msg.includes("possible secret")) {
        const detail = msg.replace(/^.*possible secret/, "Possible secret");
        if (confirm(`${detail}\n\nImport anyway? Only do this if you've reviewed the flagged file and it's a placeholder, not a real credential.`)) {
          importMut.mutate(true);
        }
        return;
      }
      toast.error("Import failed", msg);
    },
  });

  const busy = status === "running";
  const send = () => { if (draft.trim() && !busy) turnMut.mutate(draft.trim()); };

  if (!meta) return <div className="h-full flex items-center justify-center"><Spinner /></div>;

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Mobile pane switcher (desktop shows both side-by-side) */}
      <div className="md:hidden flex flex-shrink-0 border-b border-border">
        <button type="button" onClick={() => setMobileTab("chat")} className={cn("flex-1 h-9 text-xs font-medium", mobileTab === "chat" ? "text-fg border-b-2 border-accent" : "text-fg-muted")}>Chat</button>
        <button type="button" onClick={() => setMobileTab("files")} className={cn("flex-1 h-9 text-xs font-medium", mobileTab === "files" ? "text-fg border-b-2 border-accent" : "text-fg-muted")}>Files · {staged.length}</button>
      </div>
      <div className="flex-1 min-h-0 flex flex-col md:flex-row">
      {/* Chat */}
      <div className={cn("flex-1 min-w-0 min-h-0 flex-col border-border", mobileTab === "chat" ? "flex" : "hidden", "md:flex md:border-r")}>
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
          <span className="text-sm text-fg truncate flex-1" title={meta.goal}>{meta.goal}</span>
          {status && <Pill tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Pill>}
        </div>
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 space-y-3">
          <Bubble role="user" text={meta.goal} />
          {transcript.map((m, i) => <Bubble key={i} role={m.role} text={m.text} />)}
          {pendingUser.map((t, i) => <Bubble key={`p${i}`} role="user" text={t} />)}
          {busy && <div className="flex items-center gap-2 text-xs text-fg-muted"><Loader2 className="size-3.5 animate-spin" /> drafting…</div>}
          {status === "failed" && <div className="text-xs text-danger">Turn failed: {meta.error}</div>}
        </div>
        <div className="p-3 border-t border-border">
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
              rows={2}
              placeholder={busy ? "agent is working…" : status === "cancelled" ? "build cancelled" : "Reply, refine, or ask to adjust…  (⌘/Ctrl+Enter)"}
              disabled={busy || status === "cancelled"}
              className="flex-1 text-sm rounded-md bg-surface-elevated border border-border p-2 resize-none focus:border-border-strong disabled:opacity-50"
            />
            <Button onClick={send} disabled={!draft.trim() || busy || status === "cancelled"} loading={turnMut.isPending}><Send className="size-3.5" /></Button>
          </div>
        </div>
      </div>

      {/* Staging tree + viewer + actions */}
      <div className={cn("flex-1 md:flex-none md:w-[44%] md:max-w-2xl flex-col min-h-0", mobileTab === "files" ? "flex" : "hidden", "md:flex")}>
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-fg-subtle">Staged · {staged.length} file{staged.length === 1 ? "" : "s"}</span>
          <div className="ml-auto flex items-center gap-1.5">
            {(status === "running" || status === "awaiting_user") && (
              <Button size="sm" variant="ghost" onClick={() => { if (confirm("Cancel this build? Staged drafts are discarded.")) cancelMut.mutate(); }}>
                <Ban className="size-3.5" /> Cancel
              </Button>
            )}
            <Button size="sm" onClick={() => importMut.mutate(undefined)} disabled={!staged.includes("SKILL.md") || busy} loading={importMut.isPending}>
              <PackageCheck className="size-3.5" /> Approve &amp; Import
            </Button>
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          <div className="flex-shrink-0 md:w-48 border-b md:border-b-0 md:border-r border-border overflow-x-auto md:overflow-x-visible md:overflow-y-auto overscroll-contain">
            {staged.length === 0 ? <div className="p-3 text-xs text-fg-muted">No drafts yet</div> : (
              <ul className="flex md:block gap-1 md:gap-0 p-1 md:py-1 md:px-0">
                {staged.map((f) => (
                  <li key={f} className="flex-shrink-0">
                    <button onClick={() => setViewFile(f)} className={cn("flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-surface-2 font-mono whitespace-nowrap rounded md:rounded-none md:w-full", viewFile === f ? "bg-surface-2 text-fg" : "text-fg-muted")}>
                      <FileText className="size-3 flex-shrink-0 text-fg-subtle" />
                      <span className="truncate max-w-[45vw] md:max-w-none">{f}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex-1 min-w-0 min-h-0">
            {viewFile ? <FileViewer id={id} path={viewFile} rev={data?.log.length ?? 0} /> : (
              <div className="h-full flex items-center justify-center px-6 text-center">
                <EmptyState icon={<Wrench className="size-7" />} title="Staging is empty" description="As the agent drafts files they'll appear here. Review them before importing." />
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

function FileViewer({ id, path, rev }: { id: string; path: string; rev: number }) {
  // rev (log length) busts the query when the staged file changes mid-build.
  const { data, isLoading } = useQuery({ queryKey: [...qk.builder.detail(id), "file", path, rev], queryFn: () => SkillBuilderApi.file(id, path) });
  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="px-3 py-2 border-b border-border text-xs text-fg-muted font-mono">{path}</div>
      {isLoading ? <div className="flex-1 flex items-center justify-center"><Spinner /></div>
        : <pre className="flex-1 min-h-0 overflow-auto overscroll-contain p-3 text-xs font-mono text-fg whitespace-pre-wrap">{data?.content ?? ""}</pre>}
    </div>
  );
}

function Bubble({ role, text }: { role: "assistant" | "tool" | "user"; text: string }) {
  if (role === "tool") return <div className="flex items-center gap-1.5 text-[11px] text-accent font-mono"><FileText className="size-3" /> {text}</div>;
  return (
    <div className={cn("flex", role === "user" ? "justify-end" : "justify-start")}>
      <div className={cn("max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap [overflow-wrap:anywhere]", role === "user" ? "bg-accent text-fg-inverse" : "bg-surface-elevated text-fg border border-border")}>
        {text}
      </div>
    </div>
  );
}
